/**
 * Library Square — interactive map + accessible modal.
 *
 * Map uses the same AdvancedMarkerElement / numbered-pin pattern as the
 * main branches map, but zoomed into the Library Square campus.
 * Modal follows the people.js pattern: focus trap, ESC, overlay click,
 * body scroll lock, fade + scale transition.
 */

import { HIVE_MAP_STYLES } from "./map-styles.js";

// ---------------------------------------------------------------------------
// Modal helpers (mirrors people.js)
// ---------------------------------------------------------------------------

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

function getFocusable(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
    (el) => !el.closest("[hidden]") || el.closest("[hidden]") === container,
  );
}

function openModal(trigger, modal) {
  document.documentElement.style.overflow = "hidden";
  modal.removeAttribute("hidden");
  modal.classList.add("is-open");

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add("is-visible");
    });
  });

  trigger.setAttribute("aria-expanded", "true");

  const focusable = getFocusable(modal);
  if (focusable.length) {
    focusable[0].focus();
  }
}

function closeModal(trigger, modal) {
  modal.classList.remove("is-visible");

  const inner = modal.querySelector(".square-modal-inner");
  const onEnd = () => {
    inner.removeEventListener("transitionend", onEnd);
    modal.classList.remove("is-open");
    modal.setAttribute("hidden", "");
    document.documentElement.style.overflow = "";
    trigger.setAttribute("aria-expanded", "false");
    trigger.focus();
  };

  const duration = parseFloat(getComputedStyle(inner).transitionDuration);
  if (!duration) {
    onEnd();
  } else {
    inner.addEventListener("transitionend", onEnd, { once: true });
    setTimeout(onEnd, duration * 1000 + 50);
  }
}

function trapFocus(modal, e) {
  const focusable = getFocusable(modal);
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (e.key === "Tab") {
    if (e.shiftKey) {
      if (document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Modal init
// ---------------------------------------------------------------------------

document.querySelectorAll(".square-modal-trigger").forEach((trigger) => {
  const modalId = trigger.getAttribute("aria-controls");
  const modal = document.getElementById(modalId);
  if (!modal) return;

  let isOpen = false;

  trigger.addEventListener("click", () => {
    if (!isOpen) {
      isOpen = true;
      openModal(trigger, modal);
    }
  });

  const close = () => {
    if (isOpen) {
      isOpen = false;
      closeModal(trigger, modal);
    }
  };

  modal.querySelector(".square-modal-close")?.addEventListener("click", close);

  modal.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      close();
      return;
    }
    trapFocus(modal, e);
  });

  modal.addEventListener("click", (e) => {
    if (e.target === modal) {
      close();
    }
  });
});

// ---------------------------------------------------------------------------
// Tooltip helpers
// ---------------------------------------------------------------------------

function escapeHtml(str) {
  return String(str ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c],
  );
}

function buildSquareTooltip(loc) {
  let nameHtml;
  if (loc.url) {
    const targetAttr = loc.target
      ? ` target="${escapeHtml(loc.target)}" rel="noopener noreferrer"`
      : "";
    nameHtml = `<a class="branch-map-tooltip-name" href="${escapeHtml(loc.url)}"${targetAttr}>${escapeHtml(loc.name)}</a>`;
  } else if (loc.modalId) {
    nameHtml = `<button type="button" class="branch-map-tooltip-name square-modal-trigger" aria-controls="${escapeHtml(loc.modalId)}" aria-expanded="false">${escapeHtml(loc.name)}</button>`;
  } else {
    nameHtml = `<span class="branch-map-tooltip-name">${escapeHtml(loc.name)}</span>`;
  }

  const line1 = loc.addressLine1 || "";
  const line2 = loc.addressLine2 || "";
  const addressHtml =
    line1 || line2
      ? `<p class="branch-map-tooltip-address">
          ${line1 ? escapeHtml(line1) : ""}
          ${line1 && line2 ? "<br>" : ""}
          ${line2 ? escapeHtml(line2) : ""}
        </p>`
      : "";

  return `<div class="branch-map-tooltip">
    <div class="branch-map-tooltip-body">
      ${nameHtml}
      ${addressHtml}
    </div>
  </div>`;
}

// ---------------------------------------------------------------------------
// Google Maps init (called as async callback by Maps API)
// ---------------------------------------------------------------------------

let squareInfoWindow = null;
const squarePins = []; // { index, pinEl } — for toggling `.is-active`

function setActiveSquarePin(index) {
  squarePins.forEach(({ index: i, pinEl }) => {
    pinEl.classList.toggle("is-active", i === index);
  });
}

window.initSquareMap = function () {
  const canvas = document.getElementById("square-map-canvas");
  if (!canvas) return;

  const data = window.squareData || [];

  // Default center: Library Square area
  const defaultCenter = { lat: 34.74712078459153, lng: -92.26680515460274 };

  const map = new google.maps.Map(canvas, {
    zoom: 18,
    center: defaultCenter,
    mapId: "square-map",
    // 'greedy' silences the "use ⌘/Ctrl + scroll" overlay; pairing it with
    // `scrollwheel: false` keeps page scroll intact when the cursor is
    // over the map (no zoom from wheel/trackpad).
    gestureHandling: "greedy",
    scrollwheel: false,
    mapTypeControl: false,
    streetViewControl: false,
    // Suppress Google's native POI overlays — only our pins should be
    // interactive on this map.
    clickableIcons: false,
    styles: HIVE_MAP_STYLES,
  });

  // Single shared InfoWindow — reused across markers
  const infoWindow = new google.maps.InfoWindow({ maxWidth: 280 });
  squareInfoWindow = infoWindow;

  // Move focus into the tooltip when it opens so keyboard users can reach it.
  // The modal-trigger button inside the tooltip is dynamically created after the
  // initial `querySelectorAll('.square-modal-trigger')` pass, so it has no click
  // handler of its own — proxy to the matching list-item button.
  infoWindow.addListener("domready", () => {
    const tooltipTrigger = document.querySelector(
      ".branch-map-tooltip .square-modal-trigger",
    );
    if (tooltipTrigger) {
      const modalId = tooltipTrigger.getAttribute("aria-controls");
      const listTrigger = document.querySelector(
        `.square-map-item .square-modal-trigger[aria-controls="${modalId}"]`,
      );
      if (listTrigger) {
        tooltipTrigger.addEventListener("click", () => {
          infoWindow.close();
          listTrigger.click();
        });
      }
    }

    const focusTarget = document.querySelector(".branch-map-tooltip-name");
    if (focusTarget && typeof focusTarget.focus === "function") {
      focusTarget.focus();
    }
  });

  const bounds = new google.maps.LatLngBounds();
  let hasCoords = false;

  data.forEach((loc) => {
    if (!loc.lat || !loc.lng) return;

    const position = { lat: loc.lat, lng: loc.lng };
    bounds.extend(position);
    hasCoords = true;

    const pinEl = document.createElement("div");
    pinEl.className = "branch-map-pin";
    pinEl.setAttribute("aria-label", loc.name);
    pinEl.innerHTML = `<svg class="branch-map-pin-svg" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M16 0C7.2 0 0 7.2 0 16C0 27 16 42 16 42C16 42 32 27 32 16C32 7.2 24.8 0 16 0Z"/>
    </svg>
    <span class="branch-map-pin-num">${loc.index}</span>`;

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position,
      map,
      content: pinEl,
      title: loc.name,
    });

    marker.addListener("click", () => {
      infoWindow.setContent(buildSquareTooltip(loc));
      infoWindow.open({ map, anchor: marker });
      setActiveSquarePin(loc.index);

      const item = document.getElementById(`square-map-item-${loc.index}`);
      if (item) {
        document
          .querySelectorAll(".square-map-item.is-highlighted")
          .forEach((el) => {
            el.classList.remove("is-highlighted");
          });
        item.classList.add("is-highlighted");
        item.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    });

    squarePins.push({ index: loc.index, pinEl });
  });

  // Close InfoWindow / clear active pin
  map.addListener("click", () => {
    infoWindow.close();
    setActiveSquarePin(null);
  });
  infoWindow.addListener("closeclick", () => setActiveSquarePin(null));

  // Wire up list items to activate their corresponding pin on focus/hover
  document
    .querySelectorAll(".square-map-item[data-square-index]")
    .forEach((item) => {
      const idx = parseInt(item.dataset.squareIndex, 10);
      item.addEventListener("focusin", () => setActiveSquarePin(idx));
      item.addEventListener("focusout", (e) => {
        if (!item.contains(e.relatedTarget)) setActiveSquarePin(null);
      });
      item.addEventListener("mouseenter", () => setActiveSquarePin(idx));
      item.addEventListener("mouseleave", () => {
        if (!item.contains(document.activeElement)) setActiveSquarePin(null);
      });
    });

  if (hasCoords) {
    // Cap the zoom during the fit so a small bounds (or a missing pin
    // tightening the bbox unexpectedly) can never zoom past where the
    // whole campus is visible. Restored after the map settles so
    // manual zoom-in still works.
    const FIT_MAX_ZOOM = 18;

    const fit = () => {
      map.setOptions({ maxZoom: FIT_MAX_ZOOM });
      map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
      google.maps.event.addListenerOnce(map, "idle", () => {
        map.setOptions({ maxZoom: 21 });
      });
    };

    // Initial fit on the map's first 'idle' — the canvas has fully
    // painted by then, so fitBounds runs against its real dimensions.
    google.maps.event.addListenerOnce(map, "idle", fit);

    // Re-fit any time the canvas size changes after init — covers web
    // fonts swapping, --header-height shifting (site-alert.js, admin
    // bar), viewport changes, alert dismiss. Initial sync entry from
    // ResizeObserver is skipped so it doesn't race with the first-idle
    // fit; the 200ms debounce collapses the flurry of shifts into a
    // single trailing fit at the final settled size.
    let resizeTimer;
    let firstObservation = true;
    const ro = new ResizeObserver(() => {
      if (firstObservation) {
        firstObservation = false;
        return;
      }
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(fit, 200);
    });
    ro.observe(canvas);
  }
};

// Escape: close InfoWindow
document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  if (squareInfoWindow) {
    squareInfoWindow.close();
    setActiveSquarePin(null);
  }
});

// Load the Maps API only after `initSquareMap` is defined above. Doing this
// from JS (rather than an inline `<script async callback=...>` in PHP) avoids
// a race where Maps finishes loading before the bundle has parsed and fires
// the callback against an undefined function. Guarded by `__hiveMapsLoaded`
// so the API is never injected twice (Maps refuses to load more than once).
if (
  document.getElementById("square-map-canvas") &&
  !window.__hiveMapsLoaded
) {
  window.__hiveMapsLoaded = true;
  const key = window.HIVE_GMAPS_KEY || "";
  const s = document.createElement("script");
  s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&callback=initSquareMap&loading=async&libraries=marker`;
  s.async = true;
  document.head.appendChild(s);
}
